import { useRef, useState, type ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import { useNotify } from 'react-admin';
import * as XLSX from 'xlsx';
import { unzipSync } from 'fflate';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';

declare const process: {
	env: {
		REACT_APP_API_URL?: string;
	};
};

export interface COATemplateItem {
	id?: number;
	batchNumber?: string;
	parameter: string;
	classification: string;
	testMethod: string;
	uom: string;
	standardValue?: string | null;
	result?: string | null;
}

export interface COATemplate {
	id: string;
	batchNumber: string;
	productDescription: string;
	manufacturer?: string | null;
	productType?: string | null;
	countryOfOrigin?: string | null;
	specNumber?: string | null;
	reportNumber?: string | null;
	sampleSubmissionRefNumber?: string | null;
	sampleReceivedDate?: string | null;
	packaging?: string | null;
	testingPurpose?: string | null;
	client?: string | null;
	manufactureDate?: string | null;
	expiryDate?: string | null;
	description?: string;
	preparedBy?: string;
	preparedByTitle?: string;
	preparedBySignature?: string;
	preparedByDate?: string;
	releaseBy?: string;
	releaseByTitle?: string;
	releaseBySignature?: string;
	releaseByDate?: string;
	pageHeader?: string | null;
	pageFooter?: string | null;
	headerVmlXml?: string | null;
	headerImagePath?: string | null;
	pageMargins?: string | null;
	items: COATemplateItem[];
}

interface TemplateImportProps {
	onImported: () => Promise<void> | void;
	apiUrl?: string;
	className?: string;
}

type TemplateImportType = 'normal' | 'special';

const TEMPLATE_SHEET_NAME_CANDIDATES_NORMAL  = ['COATemplate', 'CoA Template'];
const TEMPLATE_SHEET_NAME_CANDIDATES_SPECIAL = ['COATemplate', 'CoA Template'];
const SUMMARY_ROW_SKIP_COUNT = 8;
const SPECIAL_TEMPLATE_SUMMARY_ROW_SKIP_COUNT = 11;

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[_\s\-/()]+/g, '');

const extractClassificationName = (value: string): string => {
	const text = String(value ?? '').trim();
	if (!text) return 'General';

	const firstLine = text.split(/\r?\n/)[0]?.trim() || text;
	return firstLine.replace(/[\s:;，、。]+$/g, '') || 'General';
};

const isParameterHeader = (value: string): boolean => {
	const normalized = normalizeHeader(value);
	return normalized === 'parameter' || normalized.includes('parameter');
};

const extractInlineLabelValue = (value: unknown): string => {
	const text = String(value ?? '').trim();
	if (!text) return '';

	const separatorIndex = text.search(/[:：]/);
	if (separatorIndex < 0) return '';

	return text.slice(separatorIndex + 1).trim();
};

const isLabeledCell = (value: unknown): boolean => {
	const text = String(value ?? '').trim();
	return /^[^:：\r\n]{1,80}[:：]/.test(text);
};

// Supported date string formats:
//   YYYY/MM/DD  YYYY-MM-DD  YYYY.MM.DD          (ISO-style, year first)
//   DD/MM/YYYY  DD-MM-YYYY  DD.MM.YYYY          (international, day first, 4-digit year)
//   MM/DD/YYYY  MM-DD-YYYY  MM.DD.YYYY          (US style, tried as fallback when DD/MM invalid)
//   DD/MM/YY    DD-MM-YY    DD.MM.YY            (2-digit year, century pivot at 50)
//   YY/MM/DD    YY-MM-DD    YY.MM.DD            (2-digit year first)
//   DD MMM YYYY  DD MMMM YYYY                   (e.g. "15 Jan 2024", "15 January 2024")
//   MMM DD, YYYY  MMMM DD, YYYY                 (e.g. "Jan 15, 2024")
//   Excel serial numbers (numeric)
//   JS Date objects
const MONTH_TEXT: Record<string, number> = {
	jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
	jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
	january: 1, february: 2, march: 3, april: 4, june: 6,
	july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

const makeIsoDate = (y: number, m: number, d: number): string => {
	if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
	if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return '';
	const date = new Date(y, m - 1, d);
	// Validate round-trip (rejects dates like Feb 30)
	if (date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) return '';
	return date.toISOString().slice(0, 10);
};

const expandYear2 = (yy: number): number => yy + (yy >= 50 ? 1900 : 2000);

const toIsoDate = (value: unknown): string => {
	if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
	if (typeof value === 'number') {
		if (!Number.isFinite(value) || value <= 0) return '';
		const jsDate = XLSX.SSF.parse_date_code(value);
		if (jsDate) {
			const result = makeIsoDate(jsDate.y, jsDate.m, jsDate.d);
			if (result) return result;
		}
	}
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return '';

		// "DD MMM YYYY" / "DD MMMM YYYY"  →  15 Jan 2024
		const ddMmmYyyy = trimmed.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
		if (ddMmmYyyy) {
			const m = MONTH_TEXT[ddMmmYyyy[2].toLowerCase()];
			if (m) {
				const result = makeIsoDate(Number(ddMmmYyyy[3]), m, Number(ddMmmYyyy[1]));
				if (result) return result;
			}
		}

		// "MMM DD, YYYY" / "MMMM DD, YYYY"  →  Jan 15, 2024
		const mmmDdYyyy = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
		if (mmmDdYyyy) {
			const m = MONTH_TEXT[mmmDdYyyy[1].toLowerCase()];
			if (m) {
				const result = makeIsoDate(Number(mmmDdYyyy[3]), m, Number(mmmDdYyyy[2]));
				if (result) return result;
			}
		}

		// Numeric separator formats: normalise . and - to /
		const normalized = trimmed.replace(/[-.]/g, '/');
		const parts = normalized.split('/').map((p) => p.trim()).filter(Boolean);
		if (parts.length === 3) {
			const p0 = Number(parts[0]);
			const p1 = Number(parts[1]);
			const p2 = Number(parts[2]);
			if (Number.isFinite(p0) && Number.isFinite(p1) && Number.isFinite(p2)) {
				// YYYY/MM/DD: 4-digit year first (or unambiguously > 31)
				if (parts[0].length === 4 || p0 > 31) {
					const result = makeIsoDate(p0, p1, p2);
					if (result) return result;
				}
				// Formats where the year is last
				if (parts[2].length === 4 || p2 > 31) {
					const y = parts[2].length === 2 ? expandYear2(p2) : p2;
					// DD/MM/YYYY: day first (p0 > 12 is unambiguous)
					if (p0 > 12) {
						const result = makeIsoDate(y, p1, p0);
						if (result) return result;
					}
					// Try DD/MM/YYYY first (international preference), then MM/DD/YYYY
					const r1 = makeIsoDate(y, p1, p0);
					if (r1) return r1;
					const r2 = makeIsoDate(y, p0, p1);
					if (r2) return r2;
				}
				// 2-digit year first: YY/MM/DD
				if (parts[0].length === 2 && p0 < 100) {
					const result = makeIsoDate(expandYear2(p0), p1, p2);
					if (result) return result;
				}
				// 2-digit year last: DD/MM/YY
				if (parts[2].length === 2 && p2 < 100) {
					const y = expandYear2(p2);
					const r1 = makeIsoDate(y, p1, p0);
					if (r1) return r1;
					const r2 = makeIsoDate(y, p0, p1);
					if (r2) return r2;
				}
			}
		}

		// Last-resort: JS Date constructor (handles ISO 8601, RFC 2822, etc.)
		const date = new Date(trimmed);
		if (!Number.isNaN(date.getTime())) {
			const y = date.getFullYear();
			if (y >= 1900 && y <= 2100) return date.toISOString().slice(0, 10);
		}
	}
	return '';
};

const getCellDisplayText = (cell: XLSX.CellObject | undefined): string => {
	if (!cell) return '';
	const formattedValue = typeof cell.w === 'string' ? cell.w.trim() : '';
	if (formattedValue) return formattedValue;

	const rawValue = cell.v;
	if (rawValue === null || rawValue === undefined) return '';
	return String(rawValue).trim();
};

const worksheetToDisplayRows = (sheet: XLSX.WorkSheet): string[][] => {
	const ref = sheet['!ref'];
	if (!ref) return [];

	const range = XLSX.utils.decode_range(ref);
	const colsMeta = sheet['!cols'] || [];

	const visibleCols: number[] = [];
	for (let c = range.s.c; c <= range.e.c; c++) {
		const colMeta = colsMeta[c];
		const isHidden = colMeta && colMeta.hidden === true;
		const isZeroWidth = colMeta && (colMeta.wpx === 0 || colMeta.wch === 0 || colMeta.width === 0);
		if (isHidden || isZeroWidth) continue;
		visibleCols.push(c);
	}

	const rows: string[][] = [];
	const rowsMeta = sheet['!rows'] || [];

	for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
		const rowMeta = rowsMeta[rowIndex];
		if (rowMeta && rowMeta.hidden === true) {
			continue;
		}

		const row: string[] = [];
		for (const columnIndex of visibleCols) {
			const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
			row.push(getCellDisplayText(sheet[cellAddress] as XLSX.CellObject | undefined));
		}
		rows.push(row);
	}

	return rows;
};

const normalizeSheetName = (value: string) => normalizeHeader(value);

// requireClassification: normal templates require chemistry/parameter in header;
// special templates only need method + uom (classification name appears in col 0 instead).
const findCoATemplateHeaderRowIndex = (rows: any[][], startRow = SUMMARY_ROW_SKIP_COUNT, requireClassification = true): number => {
	for (let r = startRow; r < rows.length; r++) {
		const row = rows[r] || [];
		const normalizedCells = row.map((cell) => normalizeHeader(String(cell ?? ''))).filter(Boolean);
		const rowText = normalizedCells.join(' ');
		const hasMethod = normalizedCells.some((cell) => (cell || '').includes('testmethod') || (cell || '').includes('method'));
		const hasUom = normalizedCells.some((cell) => (cell || '').includes('uom') || (cell || '').includes('unit'));
		const hasChemistry = normalizedCells.some((cell) => cell === 'chemistry' || cell.includes('chemistry'));
		const hasParameter = normalizedCells.some((cell) => cell === 'parameter' || cell.includes('parameter'));

		if ((hasChemistry || hasParameter || !requireClassification) && hasMethod && hasUom) {
			return r;
		}

		if (rowText.includes('chemistry') && rowText.includes('method') && rowText.includes('uom')) {
			return r;
		}
	}

	return -1;
};

const findLabeledValue = (rows: any[][], label: string, startRow = 0, endRow = rows.length - 1): unknown => {
	const target = normalizeHeader(label);
	const rowLimit = Math.max(0, Math.min(rows.length - 1, endRow));
	for (let r = Math.max(0, startRow); r <= rowLimit; r++) {
		const row = rows[r] || [];
		for (let c = 0; c < row.length; c++) {
			const current = normalizeHeader(String(row[c] ?? ''));
			if (!current || !current.includes(target)) continue;

			const inlineValue = extractInlineLabelValue(row[c]);
			if (inlineValue) return inlineValue;

			const sameRowCandidates = [row[c + 1], row[c + 2], row[c + 3], row[c + 4]];
			for (const value of sameRowCandidates) {
				if (isLabeledCell(value)) break;
				if (String(value ?? '').trim()) return value;
			}

			for (let rr = r + 1; rr <= Math.min(r + 2, rowLimit); rr++) {
				const belowRow = rows[rr] || [];
				for (let cc = c; cc <= Math.min(c + 3, belowRow.length - 1); cc++) {
					const value = belowRow[cc];
					if (String(value ?? '').trim()) return value;
				}
			}

			const inlineParts = String(row[c] ?? '').split(/[:：]/).slice(1).map((part) => part.trim()).filter(Boolean);
			if (inlineParts.length > 0) return inlineParts.join(': ');
		}
	}
	return '';
};

const findLabeledValueInRow = (row: any[], label: string, startColumn = 0): unknown => {
	const target = normalizeHeader(label);
	for (let c = Math.max(0, startColumn); c < row.length; c++) {
		const current = normalizeHeader(String(row[c] ?? ''));
		if (!current || !current.includes(target)) continue;

		const inlineValue = extractInlineLabelValue(row[c]);
		if (inlineValue) return inlineValue;

		for (let next = c + 1; next <= Math.min(c + 4, row.length - 1); next++) {
			const value = row[next];
			if (isLabeledCell(value)) break;
			if (String(value ?? '').trim()) return value;
		}
	}

	return '';
};

const collectFooterDescription = (rows: string[][], startRow: number): string => {
	const descriptionRows: string[] = [];

	for (let r = Math.max(0, startRow); r < rows.length; r++) {
		const row = rows[r] || [];
		const rowText = row.map((cell) => String(cell ?? '').trim()).filter(Boolean).join(' ').trim();
		// Blank lines can separate paragraphs within the footer (e.g. a disclaimer
		// block after the main statement). Preserve a single blank line between
		// paragraphs (skipping leading/duplicate blanks) and keep collecting until
		// a real stop marker (Prepared by/Released by/Signature/Title) is found.
		if (!rowText) {
			if (descriptionRows.length > 0 && descriptionRows[descriptionRows.length - 1] !== '') {
				descriptionRows.push('');
			}
			continue;
		}

		const normalizedText = normalizeHeader(rowText);
		if (
			normalizedText.includes('preparedby') ||
			normalizedText.includes('releasedby') ||
			normalizedText.includes('releaseby') ||
			normalizedText.includes('signature') ||
			normalizedText === 'title' ||
			normalizedText.startsWith('titlequality')
		) {
			break;
		}

		descriptionRows.push(rowText);
	}

	while (descriptionRows.length > 0 && descriptionRows[descriptionRows.length - 1] === '') {
		descriptionRows.pop();
	}

	return descriptionRows.join('\n');
};

const getTargetSheet = (workbook: XLSX.WorkBook, isSpecialTemplate = false): XLSX.WorkSheet => {
	const candidates = isSpecialTemplate
		? ['COATemplate', 'CoA Template']
		: TEMPLATE_SHEET_NAME_CANDIDATES_NORMAL;
	for (const candidateName of candidates) {
		const exactSheetName = workbook.SheetNames.find((sheetName) => normalizeSheetName(sheetName) === normalizeSheetName(candidateName));
		if (!exactSheetName) continue;

		const candidateSheet = workbook.Sheets[exactSheetName];
		if (candidateSheet) return candidateSheet;
	}

	for (const sheetName of workbook.SheetNames) {
		const sheet = workbook.Sheets[sheetName];
		if (!sheet) continue;
		const rows = worksheetToDisplayRows(sheet);
		if (findCoATemplateHeaderRowIndex(rows) >= 0) {
			return sheet;
		}
	}

	throw new Error(`Worksheet not found. Expected one of: ${[...TEMPLATE_SHEET_NAME_CANDIDATES_NORMAL, ...TEMPLATE_SHEET_NAME_CANDIDATES_SPECIAL].filter((v, i, a) => a.indexOf(v) === i).join(', ')}`);
};

const xmlUnescape = (s: string): string =>
	s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'");

const getXmlAttr = (xml: string, attr: string): string =>
	xml.match(new RegExp(`${attr}="([^"]*)"`))?.[1] ?? '';

interface XlsxHFResult {
	oddHeader: string;
	oddFooter: string;
	vmlXml: string;
	imageData: Uint8Array | null;
	imageExt: string;
	pageMargins: string | null;
}

const extractXlsxPageHeaderFooter = (
	buffer: ArrayBuffer,
	preferredSheetName: string
): XlsxHFResult => {
	const empty: XlsxHFResult = {
		oddHeader: '', oddFooter: '', vmlXml: '',
		imageData: null, imageExt: 'png', pageMargins: null,
	};
	try {
		const files = unzipSync(new Uint8Array(buffer));
		const decode = (key: string) =>
			key && files[key] ? new TextDecoder('utf-8').decode(files[key]) : '';
		const wbXml = decode('xl/workbook.xml');
		const wbRels = decode('xl/_rels/workbook.xml.rels');
		let sheetXmlKey = '';
		// Always start with the actual target sheet so H/F is read from the same
		// tab that was parsed for data (CoA Template for both normal and special types).
		const candidates = [
			preferredSheetName, 'CoA Template', 'COATemplate',
		].filter(Boolean);
		for (const name of candidates) {
			const nameEsc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const sheetTag = wbXml.match(new RegExp(`<sheet[^>]*name="${nameEsc}"[^>]*/>`))?.[0] ?? '';
			if (!sheetTag) continue;
			const rId = getXmlAttr(sheetTag, 'r:id');
			if (!rId) continue;
			const relMatch = wbRels.match(new RegExp(`Id="${rId}"[^>]*Target="([^"]+)"`));
			const target = relMatch?.[1] ?? '';
			if (!target || !files[`xl/${target}`]) continue;
			sheetXmlKey = `xl/${target}`;
			break;
		}
		if (!sheetXmlKey) {
			// Fallback: prefer any sheet that has both <headerFooter> AND a VML image reference
			const allHFSheets = Object.keys(files).filter((k) =>
				/^xl\/worksheets\/sheet\d+\.xml$/i.test(k) &&
				decode(k).includes('<headerFooter')
			);
			sheetXmlKey =
				allHFSheets.find((k) => decode(k).includes('legacyDrawingHF')) ??
				allHFSheets[0] ?? '';
		}
		if (!sheetXmlKey) return empty;
		const sheetXml = decode(sheetXmlKey);
		const oddHeader = xmlUnescape(sheetXml.match(/<oddHeader[^>]*>([\s\S]*?)<\/oddHeader>/)?.[1] ?? '');
		const oddFooter = xmlUnescape(sheetXml.match(/<oddFooter[^>]*>([\s\S]*?)<\/oddFooter>/)?.[1] ?? '');
		const marginsTag = sheetXml.match(/<pageMargins([^/]*)\/?>/)?.[0] ?? '';
		let pageMargins: string | null = null;
		if (marginsTag) {
			const n = (attr: string) => parseFloat(getXmlAttr(marginsTag, attr)) || 0;
			pageMargins = JSON.stringify({
				left: n('left'), right: n('right'), top: n('top'),
				bottom: n('bottom'), header: n('header'), footer: n('footer'),
			});
		}
		if (!(oddHeader + oddFooter).includes('&G')) {
			return { oddHeader, oddFooter, vmlXml: '', imageData: null, imageExt: 'png', pageMargins };
		}
		const vmlRId = sheetXml.match(/legacyDrawingHF[^>]*r:id="([^"]+)"/)?.[1] ?? '';
		if (!vmlRId) return { oddHeader, oddFooter, vmlXml: '', imageData: null, imageExt: 'png', pageMargins };
		const sheetRelsKey = sheetXmlKey
			.replace('worksheets/', 'worksheets/_rels/')
			.replace('.xml', '.xml.rels');
		const sheetRels = decode(sheetRelsKey);
		const vmlTarget = sheetRels.match(
			new RegExp(`Id="${vmlRId}"[^>]*Target="([^"]+vmlDrawing[^"]+)"`)
		)?.[1] ?? '';
		const vmlKey = vmlTarget ? `xl/${vmlTarget.replace('../', '')}` : '';
		if (!vmlKey || !files[vmlKey]) {
			return { oddHeader, oddFooter, vmlXml: '', imageData: null, imageExt: 'png', pageMargins };
		}
		const vmlXml = decode(vmlKey);
		const vmlRelsKey = vmlKey.replace('drawings/', 'drawings/_rels/') + '.rels';
		const vmlRels = decode(vmlRelsKey);
		const imgTarget = vmlRels.match(/Type="[^"]*\/image"[^>]*Target="([^"]+)"/)?.[1] ?? '';
		const imgKey = imgTarget ? `xl/${imgTarget.replace('../', '')}` : '';
		const imageData = imgKey && files[imgKey] ? files[imgKey] : null;
		const imageExt = imgKey.split('.').pop()?.toLowerCase() ?? 'png';
		return { oddHeader, oddFooter, vmlXml, imageData, imageExt, pageMargins };
	} catch {
		return empty;
	}
};

const uploadHeaderImageToS3 = async (
	imageData: Uint8Array,
	fileName: string,
	apiUrl: string,
	token: string | null
): Promise<string> => {
	const blob = new Blob([imageData as unknown as ArrayBuffer]);
	const form = new FormData();
	form.append('file', blob, fileName);
	const resp = await fetch(`${apiUrl}/s3files/upload?prefix=public/image`, {
		method: 'POST',
		headers: token ? { Authorization: `Bearer ${token}` } : {},
		body: form,
	});
	if (!resp.ok) throw new Error(`Image upload failed: HTTP ${resp.status}`);
	const result = await resp.json() as Record<string, unknown>;
	// Prefer the S3 key over the public URL — presign endpoint expects the key
	return String(result?.key ?? result?.Key ?? result?.url ?? result?.Url ?? '');
};

export default function TemplateImport({ onImported, apiUrl, className }: TemplateImportProps) {
	const API_URL = apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
	const notify = useNotify();
	const importFileInputRef = useRef<HTMLInputElement | null>(null);

	const [importModalOpen, setImportModalOpen] = useState(false);
	const [importTemplateType, setImportTemplateType] = useState<TemplateImportType>('normal');
	const [importFile, setImportFile] = useState<File | null>(null);
	const [importing, setImporting] = useState(false);

	const parseCoATemplateSheet = (sheet: XLSX.WorkSheet, isSpecialTemplate = false): Omit<COATemplate, 'id'> => {
		const rows = worksheetToDisplayRows(sheet);
		const skipCount = isSpecialTemplate ? SPECIAL_TEMPLATE_SUMMARY_ROW_SKIP_COUNT : SUMMARY_ROW_SKIP_COUNT;
		const headerRowIndex = findCoATemplateHeaderRowIndex(rows, skipCount, !isSpecialTemplate);
		const summaryRowEndIndex = Math.max(0, Math.min(headerRowIndex - 1, skipCount - 1));

		if (headerRowIndex < 0) {
			throw new Error('Unable to locate the COATemplate table header row.');
		}

		const headerRow = rows[headerRowIndex] || [];
		const headerMap = headerRow.reduce<Record<string, number>>((acc, cell, index) => {
			const normalized = normalizeHeader(String(cell ?? ''));
			if (normalized) acc[normalized] = index;
			return acc;
		}, {});

		const parameterIdx = 0;
		// Normal templates (4 visible cols):
		//   col 0 = parameter,  col 1 = UOM,  col 2 = Test Result,  col 3 = Test Method
		// Special templates (6 visible cols):
		//   col 0 = parameter,  col 1 = UOM,  col 2 = Test Result (raw),
		//   col 3 = Standard Value,  col 4 = Test Method,  col 5 = Result 结论  (stored as `result`)
		const uomIdx           = headerMap.uom         ?? headerMap.unit       ?? 1;
		const testResultIdx    = headerMap.testresult  ?? headerMap.result     ?? (isSpecialTemplate ? 5 : 2);
		const testMethodIdx    = headerMap.testmethod  ?? headerMap.method     ?? (isSpecialTemplate ? 4 : 3);
		const standardValueIdx = headerMap.standardvalue ?? headerMap.standard ?? headerMap.specification
			?? (isSpecialTemplate ? 3 : -1);

		if (testMethodIdx < 0 || uomIdx < 0) {
			throw new Error('Unable to locate COATemplate columns (Test Method/UOM).');
		}

		const productDescriptionRaw = isSpecialTemplate
			? (
				findLabeledValue(rows, 'Product Name', 0, summaryRowEndIndex) ||
				findLabeledValue(rows, 'Product Description', 0, summaryRowEndIndex)
			)
			: findLabeledValue(rows, 'Product Description', 0, summaryRowEndIndex);
		const manufacturerRaw = findLabeledValue(rows, 'Manufacturer', 0, summaryRowEndIndex);
		const productTypeRaw = findLabeledValue(rows, 'Product Type', 0, summaryRowEndIndex);
		const countryOfOriginRaw = findLabeledValue(rows, 'Country of Origin', 0, summaryRowEndIndex);
		const specNumberRaw = isSpecialTemplate
			? (
				findLabeledValue(rows, 'Product Code', 0, summaryRowEndIndex) ||
				findLabeledValue(rows, 'Spec Number', 0, summaryRowEndIndex)
			)
			: findLabeledValue(rows, 'Spec Number', 0, summaryRowEndIndex);
		const reportNumberRaw = findLabeledValue(rows, 'Report Number', 0, summaryRowEndIndex);
		const batchNumberRaw = findLabeledValue(rows, 'Batch Number', 0, summaryRowEndIndex);
		const sampleReceivedDateRaw = findLabeledValue(rows, 'Sample Received Date', 0, summaryRowEndIndex);
		const packagingRaw = findLabeledValue(rows, 'Packaging', 0, summaryRowEndIndex);
		const testingPurposeRaw = findLabeledValue(rows, 'Testing Purpose', 0, summaryRowEndIndex);
		const clientRaw = findLabeledValue(rows, 'Client', 0, summaryRowEndIndex);
		const manufactureDateRaw = findLabeledValue(rows, 'Manufacture Date', 0, summaryRowEndIndex);
		const expiryDateRaw = findLabeledValue(rows, 'Expiry Date', 0, summaryRowEndIndex);

		const items: COATemplateItem[] = [];
		const firstHeaderCell = String(headerRow[parameterIdx] ?? '').trim();
		let currentClassification = firstHeaderCell && !isParameterHeader(firstHeaderCell)
			? extractClassificationName(firstHeaderCell)
			: 'General';
		let emptyRowStreak = 0;
		let footerStartIndex = rows.length;

		for (let i = headerRowIndex + 1; i < rows.length; i++) {
			const row = rows[i] || [];
			const parameterRaw = parameterIdx >= 0 ? String(row[parameterIdx] ?? '').trim() : '';
			// A cell may contain multiple lines (e.g. "Phthalates\r\n- DEHP …"); merge them into one item.
			const parameter = parameterRaw
				.split(/\r?\n/)
				.map((part) => part.trim())
				.filter(Boolean)
				.join(' ');
			const testMethod = testMethodIdx >= 0 ? String(row[testMethodIdx] ?? '').trim() : '';
			const uom = uomIdx >= 0 ? String(row[uomIdx] ?? '').trim() : '';
			const testResult = testResultIdx >= 0 ? String(row[testResultIdx] ?? '').trim() : '';
			const standardValue = isSpecialTemplate && standardValueIdx >= 0
				? String(row[standardValueIdx] ?? '').trim()
				: '';

			const firstCell = String(row[0] ?? '').trim();

			const normalizedUom = normalizeHeader(uom || '');
			const normalizedMethod = normalizeHeader(testMethod || '');
			const normalizedResult = normalizeHeader(testResult || '');

			// Require at least 2 of the 3 columns to look like header labels.
			// Using .some() (any 1) caused false positives: rows like "Foreign Matter / AsureQuality Method"
			// triggered the check because the test-method text contained the word "method".
			const looksLikeHeaderInColumns = [
				(normalizedUom || '').includes('uom') || (normalizedUom || '').includes('unit'),
				(normalizedMethod || '').includes('testmethod') || (normalizedMethod || '').includes('method'),
				(normalizedResult || '').includes('testresult') || (normalizedResult || '').includes('result'),
			].filter(Boolean).length >= 2;
			const onlyFirstCellHasText = Boolean(firstCell && !testMethod && !uom && !testResult);

			if (firstCell && isParameterHeader(firstCell) && looksLikeHeaderInColumns) {
				continue;
			}

			if (firstCell && !isParameterHeader(firstCell) && (looksLikeHeaderInColumns || onlyFirstCellHasText)) {
				// A genuine classification heading (e.g. "Chemistry") is immediately followed by
				// item rows. If the next row is blank, this text is actually the start of the
				// trailing footer/description block, not a new section — stop parsing items here
				// so the paragraph isn't silently discarded as a bogus classification name.
				const nextRow = rows[i + 1] || [];
				const nextRowHasContent = nextRow.some((cell) => String(cell ?? '').trim());
				if (onlyFirstCellHasText && !looksLikeHeaderInColumns && !nextRowHasContent) {
					footerStartIndex = i;
					break;
				}

				currentClassification = extractClassificationName(firstCell);
				continue;
			}

			const normalizedParameter = normalizeHeader(parameter);
			const isMetaRow = [
				'productdescription',
				'batchnumber',
				'specnumber',
				'samplesubmissionrefnumber',
				'manufacturedate',
				'expirydate',
			].includes(normalizedParameter);

			if (!parameter && !testMethod && !uom && !testResult) {
				emptyRowStreak += 1;
				if (items.length > 0 && emptyRowStreak >= 1) {
					footerStartIndex = i + 1;
					break;
				}
				continue;
			}

			emptyRowStreak = 0;

			if (isMetaRow) {
				continue;
			}

			items.push({
				parameter: parameter || testMethod || 'N/A',
				classification: currentClassification,
				testMethod: testMethod || parameter || 'N/A',
				uom: uom || '-',
				...(isSpecialTemplate && {
					standardValue: standardValue || null,
					result: testResult || null,
				}),
			});
		}

		const footerSearchStart = Math.max(0, footerStartIndex);
		const descriptionRaw = collectFooterDescription(rows, footerSearchStart);
		const preparedByRaw = findLabeledValue(rows, 'Prepared by', footerSearchStart);
		const preparedByTitleRaw = findLabeledValue(rows, 'Title', footerSearchStart);
		const preparedBySignatureRaw = findLabeledValue(rows, 'Signature', footerSearchStart);
		const preparedByDateRaw =
			findLabeledValue(rows, 'Prepared By Date', footerSearchStart) ||
			findLabeledValue(rows, 'Prepared Date', footerSearchStart);
		const releaseByRaw =
			findLabeledValue(rows, 'Released by', footerSearchStart) ||
			findLabeledValue(rows, 'Release by', footerSearchStart);
		const releaseByTitleRaw = (() => {
			for (let r = footerSearchStart; r < rows.length; r++) {
				const row = rows[r] || [];
				const releaseColumnIndex = row.findIndex((cell) => {
					const normalized = normalizeHeader(String(cell ?? ''));
					return normalized.includes('releasedby') || normalized.includes('releaseby');
				});

				if (releaseColumnIndex < 0) continue;

				for (let rr = r + 1; rr <= Math.min(r + 2, rows.length - 1); rr++) {
					const titleValue = findLabeledValueInRow(rows[rr] || [], 'Title', releaseColumnIndex);
					if (String(titleValue ?? '').trim()) return titleValue;
				}
			}

			return '';
		})();
		const releaseBySignatureRaw = (() => {
			for (let r = footerSearchStart; r < rows.length; r++) {
				const row = rows[r] || [];
				const releaseColumnIndex = row.findIndex((cell) => {
					const normalized = normalizeHeader(String(cell ?? ''));
					return normalized.includes('releasedby') || normalized.includes('releaseby');
				});

				if (releaseColumnIndex < 0) continue;

				for (let rr = r + 1; rr <= Math.min(r + 3, rows.length - 1); rr++) {
					const signatureValue = findLabeledValueInRow(rows[rr] || [], 'Signature', releaseColumnIndex);
					if (String(signatureValue ?? '').trim()) return signatureValue;
				}
			}

			return '';
		})();
		const releaseByDateRaw =
			findLabeledValue(rows, 'Release By Date', footerSearchStart) ||
			findLabeledValue(rows, 'Released Date', footerSearchStart) ||
			findLabeledValue(rows, 'Release Date', footerSearchStart);

		return {
			productDescription: String(productDescriptionRaw ?? '').trim(),
			manufacturer: String(manufacturerRaw ?? '').trim(),
			productType: String(productTypeRaw ?? '').trim(),
			countryOfOrigin: String(countryOfOriginRaw ?? '').trim(),
			specNumber: String(specNumberRaw ?? '').trim(),
			reportNumber: String(reportNumberRaw ?? '').trim(),
			batchNumber: String(batchNumberRaw ?? '').trim(),
			sampleSubmissionRefNumber: '',
			sampleReceivedDate: toIsoDate(sampleReceivedDateRaw),
			packaging: String(packagingRaw ?? '').trim(),
			testingPurpose: String(testingPurposeRaw ?? '').trim(),
			client: String(clientRaw ?? '').trim(),
			manufactureDate: toIsoDate(manufactureDateRaw),
			expiryDate: toIsoDate(expiryDateRaw),
			description: String(descriptionRaw ?? '').trim(),
			preparedBy: String(preparedByRaw ?? '').trim(),
			preparedByTitle: String(preparedByTitleRaw ?? '').trim(),
			preparedBySignature: String(preparedBySignatureRaw ?? '').trim(),
			preparedByDate: toIsoDate(preparedByDateRaw),
			releaseBy: String(releaseByRaw ?? '').trim(),
			releaseByTitle: String(releaseByTitleRaw ?? '').trim(),
			releaseBySignature: String(releaseBySignatureRaw ?? '').trim(),
			releaseByDate: toIsoDate(releaseByDateRaw),
			items,
		};
	};

	const handleSelectImportFile = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		setImportFile(file);
	};

	const handleConfirmImport = async () => {
		const file = importFile;
		if (!file) return;

		setImporting(true);
		try {
			const isSpecialTemplate = importTemplateType === 'special';
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
			const targetSheet = getTargetSheet(workbook, isSpecialTemplate);
			const targetSheetName = workbook.SheetNames.find((n) => workbook.Sheets[n] === targetSheet) ?? '';
			const xlsxHF = extractXlsxPageHeaderFooter(arrayBuffer, targetSheetName);
			let headerImagePath: string | null = '/images/template_header.png';
			if (xlsxHF.imageData) {
				try {
					headerImagePath = await uploadHeaderImageToS3(
						xlsxHF.imageData,
						`coa_header_${Date.now()}.${xlsxHF.imageExt}`,
						API_URL,
						localStorage.getItem('token')
					);
				} catch (imgErr) {
					console.warn('Header image upload skipped:', imgErr);
				}
			}
			const parsedSpec = parseCoATemplateSheet(targetSheet, isSpecialTemplate);

			parsedSpec.sampleSubmissionRefNumber = String(
				findLabeledValue(worksheetToDisplayRows(targetSheet), 'Sample Submission Ref Number', 0, SUMMARY_ROW_SKIP_COUNT - 1) ||
				findLabeledValue(worksheetToDisplayRows(targetSheet), 'Sample Submission Ref Number (s)', 0, SUMMARY_ROW_SKIP_COUNT - 1) ||
				parsedSpec.sampleSubmissionRefNumber ||
				''
			).trim();

			const missingFields: string[] = [];
			if (!parsedSpec.batchNumber) missingFields.push('Batch Number');
			if (!parsedSpec.productDescription) missingFields.push('Product Description');
			if (parsedSpec.items.length === 0) missingFields.push('Template Items');

			if (missingFields.length > 0) {
				throw new Error(`Unable to parse required fields from COATemplate tab: ${missingFields.join(', ')}`);
			}

			const dto = {
				BatchNumber: parsedSpec.batchNumber,
				ProductDescription: parsedSpec.productDescription,
				Manufacturer: parsedSpec.manufacturer || null,
				ProductType: parsedSpec.productType || null,
				CountryOfOrigin: parsedSpec.countryOfOrigin || null,
				SpecNumber: parsedSpec.specNumber || null,
				ReportNumber: parsedSpec.reportNumber || null,
				SampleSubmissionRefNumber: parsedSpec.sampleSubmissionRefNumber || null,
				SampleReceivedDate: parsedSpec.sampleReceivedDate || null,
				Packaging: parsedSpec.packaging || null,
				TestingPurpose: parsedSpec.testingPurpose || null,
				Client: parsedSpec.client || null,
				ManufactureDate: parsedSpec.manufactureDate || null,
				ExpiryDate: parsedSpec.expiryDate || null,
				Description: parsedSpec.description,
				PreparedBy: parsedSpec.preparedBy,
				PreparedByTitle: parsedSpec.preparedByTitle,
				PreparedBySignature: parsedSpec.preparedBySignature,
				PreparedByDate: parsedSpec.preparedByDate || null,
				ReleaseBy: parsedSpec.releaseBy,
				ReleaseByTitle: parsedSpec.releaseByTitle,
				ReleaseBySignature: parsedSpec.releaseBySignature,
				ReleaseByDate: parsedSpec.releaseByDate || null,
				PageHeader: xlsxHF.oddHeader || null,
				PageFooter: xlsxHF.oddFooter || null,
				HeaderVmlXml: xlsxHF.vmlXml || null,
				HeaderImagePath: headerImagePath,
				PageMargins: xlsxHF.pageMargins || null,
				Items: parsedSpec.items.map((item) => ({
					Parameter: item.parameter,
					Classification: item.classification,
					TestMethod: item.testMethod,
					Uom: item.uom,
					StandardValue: item.standardValue ?? null,
					Result: item.result ?? null,
				})),
			};

			const token = localStorage.getItem('token');
			const response = await fetch(`${API_URL}/COATemplates`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify(dto),
			});

			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || `HTTP ${response.status}`);
			}

			notify(`Import success: ${parsedSpec.items.length} template items imported`, { type: 'success' });
			await onImported();

			setImportModalOpen(false);
			setImportFile(null);
			if (importFileInputRef.current) {
				importFileInputRef.current.value = '';
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unknown error';
			notify(`Import failed: ${message}`, { type: 'error' });
		} finally {
			setImporting(false);
		}
	};

	const handleCloseModal = () => {
		if (importing) return;
		setImportModalOpen(false);
		setImportFile(null);
		if (importFileInputRef.current) {
			importFileInputRef.current.value = '';
		}
	};

	return (
		<div className={className}>
			<Button
				type="button"
				variant="outline"
				className="border-blue-300 text-blue-700 hover:bg-blue-50"
				onClick={() => {
					setImportTemplateType('normal');
					setImportFile(null);
					setImportModalOpen(true);
				}}
			>
				<Upload className="mr-2 size-4" />
				Import
			</Button>

			{importModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
					<div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
						<div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
							<h3 className="text-lg font-semibold text-slate-900">Import Template</h3>
							<Button type="button" variant="ghost" onClick={handleCloseModal}>
								Close
							</Button>
						</div>

						<div className="space-y-4 px-5 py-4">
							<div className="space-y-2">
								<Label className="text-sm text-slate-700">1. Select Template Type</Label>
								<select
									className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
									value={importTemplateType}
									onChange={(e) => setImportTemplateType(e.target.value as TemplateImportType)}
									disabled={importing}
								>
									<option value="normal">Normal Template</option>
									<option value="special">Special Template</option>
								</select>
								<p className="text-xs text-slate-500">
									{importTemplateType === 'special'
										? 'Special template: metadata is read from the first 11 rows of the COATemplate sheet.'
										: 'Normal template: standard COATemplate layout.'}
								</p>
							</div>

							<div className="space-y-2">
								<Label className="text-sm text-slate-700">2. Choose File</Label>
								<input
									ref={importFileInputRef}
									type="file"
									accept=".xlsx,.xls"
									className="sr-only"
									onChange={handleSelectImportFile}
									disabled={importing}
								/>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => importFileInputRef.current?.click()}
										disabled={importing}
									>
										Choose File
									</Button>
									<span className="text-sm text-slate-600">
										{importFile ? importFile.name : 'No file selected'}
									</span>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
							<Button
								type="button"
								variant="ghost"
								onClick={handleCloseModal}
								disabled={importing}
							>
								Cancel
							</Button>
							<Button
								type="button"
								className="bg-blue-600 text-white hover:bg-blue-700"
								onClick={handleConfirmImport}
								disabled={importing || !importFile}
							>
								{importing ? 'Importing...' : 'Import and Parse'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
